import {
  Loader2, ArrowRight, MessageCircle, CheckCircle2,
  Smartphone, WifiOff, RefreshCw, Sparkles, Send,
  Phone, AlertTriangle, XCircle,
} from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input }  from '@/components/ui/input';
import { whatsappApi, companiesApi } from '@/lib/api';
import { useAppStore } from '@/store/app.store';
import type { WhatsappInstanceDto } from '@/types/api';

import { WhatsappQrPanel } from '../components/whatsapp-qr-panel';

type Phase    = 'loading' | 'preview' | 'creating' | 'scanning' | 'connected' | 'disconnected' | 'timeout';
type SendStep = 'idle' | 'sending' | 'sent' | 'error';

interface CompanyRef { id: string; ruc: string; razonSocial: string; }

interface StepWhatsappProps {
  tenantName: string;
  company:    CompanyRef | null;
  onNext:     () => void;
  onBack:     () => void;
  onSkip:     () => void;
  /** Oculta la burbuja de preview para uso en modales con espacio reducido */
  compact?:   boolean;
}

interface PreviewData {
  text:          string;
  to:            string;
  templateName:  string;
  sentThisMonth: number;
}

// Fallback local mientras carga o si falla el API
const BIENVENIDA_FALLBACK =
  '¡Hola *{{contactNombre}}*! 👋\n\n' +
  'Bienvenido a *{{tenantName}}*. A partir de ahora *{{razonSocial}}* (RUC: {{ruc}}) cuenta con un equipo especializado cuidando cada detalle tributario.\n\n' +
  '✅ Recordatorios anticipados de vencimientos\n' +
  '✅ Seguimiento de sus obligaciones tributarias\n' +
  '✅ Reportes periódicos de cumplimiento\n\n' +
  'Ante cualquier consulta, estamos para usted.\n' +
  '📞 {{tenantContacto}}';

function buildFallback(tenantName: string, company: CompanyRef | null): string {
  return BIENVENIDA_FALLBACK
    .replace('{{contactNombre}}', 'el responsable')
    .replace('{{tenantName}}',   tenantName || 'Tu estudio')
    .replace('{{razonSocial}}',  company?.razonSocial || 'la empresa')
    .replace('{{ruc}}',          company?.ruc || '—')
    .replace('{{tenantContacto}}', '(tu teléfono)');
}

function renderWhatsapp(text: string) {
  return text.split('\n').map((line, li) => (
    <span key={li} className="block min-h-[1em]">
      {line.split('*').map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>,
      )}
    </span>
  ));
}

export function StepWhatsapp({ tenantName, company, onNext, onBack, onSkip, compact = false }: StepWhatsappProps) {
  const user = useAppStore(s => s.user);

  const [phase,    setPhase]    = useState<Phase>('loading');
  const [instanceId,  setInstanceId]  = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null);
  const [qrBase64,    setQrBase64]    = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(120);

  // Preview del template
  const [previewData,    setPreviewData]    = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [noPhone,        setNoPhone]        = useState(false);   // empresa sin contactTelefono

  // Envío
  const [sendPhone, setSendPhone] = useState('');
  const [sendStep,  setSendStep]  = useState<SendStep>('idle');
  const [sendError, setSendError] = useState('');

  // ── Cargar preview real desde API ───────────────────────────────────────
  useEffect(() => {
    if (!company?.id) return;
    setPreviewLoading(true);
    companiesApi.notifyPreview(company.id, 'BIENVENIDA')
      .then(({ data }) => {
        setPreviewData(data);
        setSendPhone(data.to);
        setNoPhone(false);
      })
      .catch((err: { response?: { status?: number } }) => {
        if (err.response?.status === 400) setNoPhone(true); // sin contactTelefono
      })
      .finally(() => { setPreviewLoading(false); });
  }, [company?.id]);

  // ── Verificar instancia al montar ────────────────────────────────────────
  useEffect(() => {
    whatsappApi.list()
      .then(({ data: instances }) => {
        const first = instances[0];
        if (!first) { setPhase('preview'); return; }
        applyInstanceState(first);
      })
      .catch(() => { setPhase('preview'); });
   
  }, []);

  // En modo compact (dialog): si no hay instancia, auto-crear sin pasos intermedios
  useEffect(() => {
    if (compact && phase === 'preview') {
      void startConnect();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compact, phase]);

  function applyInstanceState(inst: WhatsappInstanceDto) {
    setInstanceId(inst.id);
    if (inst.status === 'CONNECTED') {
      setPhoneNumber(inst.phoneNumber);
      setPhase('connected');
    } else {
      setSecondsLeft(120);
      setQrBase64(null);
      setPhase('scanning');
    }
  }

  // ── QR polling ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'scanning' || !instanceId) return;

    whatsappApi.getQr(instanceId)
      .then(({ data }) => { if (data.base64) setQrBase64(data.base64); })
      .catch(() => {});

    const qrInterval = setInterval(() => { void (async () => {
      try {
        const { data: qr } = await whatsappApi.getQr(instanceId);
        if (qr.base64) {
          setQrBase64(qr.base64);
        } else {
          const { data: instances } = await whatsappApi.list();
          const inst = instances.find(i => i.id === instanceId);
          if (inst?.status === 'CONNECTED') {
            setPhoneNumber(inst.phoneNumber);
            setPhase('connected');
          }
        }
      } catch { /* reintento en el siguiente ciclo */ }
    })(); }, 5000);

    const countdown = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { setPhase('timeout'); return 0; }
        return s - 1;
      });
    }, 1000);

    return () => { clearInterval(qrInterval); clearInterval(countdown); };
  }, [phase, instanceId]);

  // ── Crear instancia ──────────────────────────────────────────────────────
  const startConnect = useCallback(async () => {
    setPhase('creating');
    try {
      const { data: inst } = await whatsappApi.create({
        displayName:  `${tenantName} — Principal`,
        notifyEmail:  user?.email ?? undefined,
      });
      setInstanceId(inst.id);
      setSecondsLeft(120);
      setQrBase64(null);
      setPhase('scanning');
    } catch (err) {
      const e = err as { response?: { status?: number } };
      if (e.response?.status === 409) {
        try {
          const { data: instances } = await whatsappApi.list();
          const first = instances[0];
          if (first) { applyInstanceState(first); return; }
        } catch { /* reintento en el siguiente ciclo */ }
      }
      toast.error('No se pudo preparar la instancia de WhatsApp. Intenta de nuevo.');
      setPhase('preview');
    }
   
  }, [tenantName, user?.email]);

  function retry() {
    setQrBase64(null);
    setSecondsLeft(120);
    setPhase('scanning');
  }

  // ── Enviar mensaje ───────────────────────────────────────────────────────
  async function handleSend() {
    if (!company?.id) return;
    setSendStep('sending');
    setSendError('');
    try {
      await companiesApi.notifySend(company.id, {
        type:     'BIENVENIDA',
        customTo: sendPhone || undefined,
      });
      setSendStep('sent');
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } };
      setSendError(e.response?.data?.message ?? 'No se pudo enviar el mensaje');
      setSendStep('error');
    }
  }

  // ── Spinner mientras carga o crea ────────────────────────────────────────
  if (phase === 'loading' || phase === 'creating') {
    return (
      <div className="space-y-6">
        <Header />
        <div className="flex flex-col items-center gap-3 py-10">
          <Loader2 className="h-7 w-7 animate-spin text-[#2980b9]" />
          <p className="text-sm text-muted-foreground">
            {phase === 'loading' ? 'Verificando estado de WhatsApp...' : 'Preparando instancia...'}
          </p>
        </div>
      </div>
    );
  }

  const bubbleText = previewData?.text ?? buildFallback(tenantName, company);
  const templateName = previewData?.templateName ?? 'Bienvenida al servicio';

  return (
    <div className="space-y-5">
      <Header />

      {/* ── Badge + Burbuja de preview (oculto en modo compact) ────────── */}
      {!compact && (
        <>
          <div className="flex items-start gap-2 rounded-lg bg-[#eaf2fa] px-3 py-2.5">
            <Sparkles className="h-4 w-4 shrink-0 text-[#1f5d86] mt-0.5" />
            <p className="text-xs text-[#1f5d86] leading-snug">
              <span className="font-semibold">Mensaje personalizado real</span>
              {phase === 'connected'
                ? ' · Tu cliente recibirá exactamente este mensaje. Puedes enviárselo ahora mismo o configurar el envío automático.'
                : ' · Así recibirá tu cliente el mensaje de bienvenida. Conéctate para poder enviárselo.'
              }
              {' '}Edita las plantillas en <span className="font-semibold">Configuración → Plantillas</span>.
            </p>
          </div>

          <div className="rounded-xl border border-[#1f5d86]/12 bg-white shadow-xs p-4 space-y-3">
            <div className="flex items-center gap-2 pb-3 border-b">
              <div className="h-9 w-9 rounded-full bg-[#1f5d86] flex items-center justify-center shrink-0">
                <Smartphone className="h-4 w-4 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{tenantName || 'Tu estudio'}</p>
                <p className="text-xs text-muted-foreground">
                  {previewLoading ? 'Cargando plantilla...' : `Plantilla · ${templateName}`}
                </p>
              </div>
              {previewData && (
                <span className="text-[10px] rounded-full bg-[#1d9e75]/10 text-[#1d9e75] px-2 py-0.5 font-medium shrink-0">
                  Datos reales
                </span>
              )}
            </div>
            {previewLoading ? (
              <div className="space-y-2 py-2">
                <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
                <div className="h-3 bg-muted rounded animate-pulse w-full" />
                <div className="h-3 bg-muted rounded animate-pulse w-5/6" />
                <div className="h-3 bg-muted rounded animate-pulse w-2/3" />
              </div>
            ) : (
              <div className="rounded-lg bg-[#eaf2fa]/60 p-3 text-sm text-foreground leading-relaxed space-y-0.5">
                {renderWhatsapp(bubbleText)}
              </div>
            )}
            {noPhone && !previewLoading && (
              <div className="flex items-start gap-2 rounded-lg border border-[#e8a547]/30 bg-[#e8a547]/8 p-2.5">
                <AlertTriangle className="h-3.5 w-3.5 text-[#e8a547] shrink-0 mt-0.5" />
                <p className="text-[11px] text-foreground/70 leading-snug">
                  La empresa no tiene teléfono de contacto configurado. Para enviar, ingresa el número manualmente.
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* ── Sección de envío (solo cuando conectado) ────────────────────── */}
      {phase === 'connected' && company && (
        <div className="rounded-xl border border-[#1f5d86]/20 bg-[#eaf2fa]/30 p-4 space-y-3">

          {sendStep === 'sent' ? (
            /* Éxito */
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-[#1d9e75] shrink-0" />
              <div>
                <p className="text-sm font-semibold text-foreground">¡Mensaje enviado!</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  El mensaje de bienvenida fue enviado a <span className="font-mono">{sendPhone}</span>.
                </p>
              </div>
            </div>
          ) : (
            <>
              <p className="text-xs font-semibold text-[#1f5d86] uppercase tracking-wide">
                Enviar ahora a tu cliente
              </p>

              {previewData?.sentThisMonth ? (
                <div className="flex items-center gap-2 text-xs text-[#e8a547]">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Ya enviado {previewData.sentThisMonth} {previewData.sentThisMonth === 1 ? 'vez' : 'veces'} este mes
                </div>
              ) : null}

              {/* Teléfono destino */}
              <div className="space-y-1.5">
                <label className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Phone className="h-3 w-3" />
                  WhatsApp del contacto principal
                </label>
                <div className="flex gap-2">
                  <span className="flex items-center px-3 rounded-l-md border border-r-0 bg-muted text-sm text-muted-foreground">
                    +51
                  </span>
                  <Input
                    value={sendPhone}
                    onChange={e => { setSendPhone(e.target.value.replace(/\D/g, '')); }}
                    placeholder="987654321"
                    className="font-mono rounded-l-none flex-1"
                    maxLength={9}
                  />
                </div>
                {previewData?.to && (
                  <p className="text-[10px] text-muted-foreground">
                    Número registrado en la empresa. Puedes editarlo si es necesario.
                  </p>
                )}
              </div>

              {/* Error de envío */}
              {sendStep === 'error' && (
                <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-2.5">
                  <XCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                  <p className="text-xs text-destructive">{sendError}</p>
                </div>
              )}

              {/* Botón enviar */}
              <Button
                onClick={handleSend}
                disabled={sendStep === 'sending' || !sendPhone}
                className="w-full gap-2"
                style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
              >
                {sendStep === 'sending'
                  ? <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</>
                  : <><Send className="h-4 w-4" /> Enviar mensaje de bienvenida</>
                }
              </Button>
            </>
          )}
        </div>
      )}

      {/* ── Estado de conexión ───────────────────────────────────────────── */}
      {phase === 'preview' && (
        <Button
          onClick={startConnect}
          className="w-full h-11 gap-2"
          style={{ background: 'linear-gradient(135deg, #1f5d86 0%, #2980b9 100%)' }}
        >
          <MessageCircle className="h-4 w-4" />
          Conectar WhatsApp para enviar este mensaje
        </Button>
      )}

      {(phase === 'scanning' || phase === 'timeout') && (
        <WhatsappQrPanel
          qrBase64={qrBase64}
          secondsLeft={secondsLeft}
          onRetry={retry}
          isTimedOut={phase === 'timeout'}
        />
      )}

      {phase === 'connected' && (
        <div className="rounded-xl border border-[#1d9e75]/30 bg-[#1d9e75]/5 px-4 py-3 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-[#1d9e75] shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">WhatsApp conectado</p>
            {phoneNumber && <p className="text-xs font-mono text-[#1f5d86] mt-0.5">{phoneNumber}</p>}
          </div>
        </div>
      )}

      {phase === 'disconnected' && (
        <div className="rounded-xl border border-[#e8a547]/30 bg-[#e8a547]/10 px-4 py-3 flex items-center gap-3">
          <WifiOff className="h-5 w-5 text-[#e8a547] shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-foreground">Instancia desconectada</p>
            <p className="text-xs text-muted-foreground mt-0.5">Escanea el QR para reactivarla.</p>
          </div>
          <Button
            size="sm" variant="outline"
            onClick={() => { setSecondsLeft(120); setPhase('scanning'); }}
            className="shrink-0 gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Ver QR
          </Button>
        </div>
      )}

      {/* ── Navegación ───────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between pt-1">
        {phase !== 'connected'
          ? <Button type="button" variant="ghost" size="sm" onClick={onBack} className="text-muted-foreground">Atrás</Button>
          : <span />
        }
        {phase === 'connected'
          ? (
            <Button
              onClick={onNext}
              variant={sendStep === 'sent' ? 'default' : 'outline'}
              className="ml-auto gap-2"
              style={sendStep === 'sent' ? { background: 'linear-gradient(135deg, #1f5d86 0%, #2980b9 100%)' } : {}}
            >
              {sendStep === 'sent' ? 'Continuar' : 'Continuar sin enviar'}
              <ArrowRight className="h-4 w-4" />
            </Button>
          )
          : (
            <button
              onClick={onSkip}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Saltar — configurar WhatsApp después
            </button>
          )
        }
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#eaf2fa]">
        <MessageCircle className="h-5 w-5 text-[#1f5d86]" />
      </div>
      <div>
        <h2 className="text-xl font-bold text-foreground">Envía alertas por WhatsApp</h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          Avisa a tus clientes de vencimientos, deudas y más — de forma automática y personalizada.
        </p>
      </div>
    </div>
  );
}
